/*
 * Applies a project's appearance (palette, corners) and theme to a subtree.
 *
 * The theme becomes CSS custom properties on this element, `data-theme`
 * says light or dark for the token rules that key on it, and the bundled
 * fonts are loaded here so a panel without internet has them.
 */

import type { ComponentChildren, JSX } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import '@fontsource-variable/inter';
import '@fontsource-variable/jost';
import '@fontsource-variable/roboto-condensed';
import { hmiAppearanceAttributes } from '../appearanceModel';
import { resolveTheme, themeCssVars, type ResolvedTheme } from '../themeModel';
import './stage.css';

function readSystemDark(): boolean {
  if (typeof document === 'undefined') return true;
  const attr = document.documentElement.getAttribute('data-theme');
  if (attr === 'dark' || attr === 'light') return attr === 'dark';
  return !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

/** Light or dark of the surroundings: the app's data-theme attribute in the
 *  cloud, the system setting in the standalone runtime. */
export function useSystemDark(): boolean {
  const [dark, setDark] = useState(readSystemDark);
  useEffect(() => {
    const mo = new MutationObserver(() => setDark(readSystemDark()));
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    const onMq = () => setDark(readSystemDark());
    mq?.addEventListener('change', onMq);
    return () => { mo.disconnect(); mq?.removeEventListener('change', onMq); };
  }, []);
  return dark;
}

export function useResolvedTheme(appearance: unknown, scheme?: 'light' | 'dark' | null): ResolvedTheme | null {
  const systemDark = useSystemDark();
  const theme = (appearance as { theme?: unknown } | undefined)?.theme;
  return theme ? resolveTheme(theme, systemDark, scheme ?? undefined) : null;
}

/** Light or dark a scope shows: the device's choice, else the theme's mode,
 *  else the surroundings. */
export function useEffectiveScheme(appearance: unknown, scheme?: 'light' | 'dark' | null): 'light' | 'dark' {
  const systemDark = useSystemDark();
  const theme = useResolvedTheme(appearance, scheme);
  return scheme ?? theme?.scheme ?? (systemDark ? 'dark' : 'light');
}

/** Light or dark picked with the toggle on this device, kept in browser
 *  storage. Storage may be missing (private window, kiosk without profile);
 *  then the choice lasts until the page reloads. */
export function useStoredScheme(storageKey: string): ['light' | 'dark' | null, (s: 'light' | 'dark') => void] {
  const [scheme, setScheme] = useState<'light' | 'dark' | null>(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v === 'light' || v === 'dark' ? v : null;
    } catch { return null; }
  });
  const set = useCallback((s: 'light' | 'dark') => {
    setScheme(s);
    try { localStorage.setItem(storageKey, s); } catch { /* bleibt bis zum Neuladen */ }
  }, [storageKey]);
  return [scheme, set];
}

interface Props extends Omit<JSX.HTMLAttributes<HTMLDivElement>, 'style'> {
  appearance: unknown;
  /** Light or dark chosen on this device; wins over the theme's mode. */
  scheme?: 'light' | 'dark' | null;
  style?: Record<string, string>;
  children?: ComponentChildren;
}

export function ThemeScope({ appearance, scheme, style, children, class: cls, ...rest }: Props) {
  const theme = useResolvedTheme(appearance, scheme);
  const vars = theme ? themeCssVars(theme) : {};
  return (
    <div
      {...rest}
      {...hmiAppearanceAttributes(appearance)}
      data-theme={theme?.scheme ?? scheme ?? undefined}
      data-hmi-themed={theme ? '' : undefined}
      class={`hv-theme-scope${cls ? ` ${String(cls)}` : ''}`}
      style={{ ...vars, ...(style ?? {}) }}
    >
      {children}
    </div>
  );
}
